'use client'

import React, { Suspense, memo, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import {
  useRecipientSectionQueries,
  useSuspenseRecipientDashboard,
} from '@/hooks/usaspending'

import { QueryErrorBoundary } from '../../DataErrorBoundary'
import { SectionCard, SectionSkeleton, useDetailFilter } from '../../shared'
import { RankingUnavailable } from '../../shared/ServiceUnavailable'
import { useRecipientDetail } from '../context'

interface AgencyItem {
  code?: string
  name: string
  amount: number
  percentage: number
}

/**
 * Format currency with appropriate suffix (B, M, K)
 */
function formatCurrency(amount: number): string {
  if (amount >= 1_000_000_000) {
    return `$${(amount / 1_000_000_000).toFixed(1)}B`
  }
  if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`
  }
  if (amount >= 1_000) {
    return `$${(amount / 1_000).toFixed(0)}K`
  }
  return `$${amount.toFixed(0)}`
}

// Hoisted static class strings (rendering-hoist-jsx)
const BAR_CLASS =
  'absolute inset-y-0 left-0 transition-all bg-blue-500/20 dark:bg-blue-500/30'
const ROW_CLASS =
  'group relative flex items-center px-2 py-1 transition-colors hover:bg-primary-50 dark:hover:bg-primary-800/50'

/**
 * RecipientDetail.TopAgencies
 *
 * Data-table display of top awarding agencies with inline bar indicators.
 * Clickable rows navigate to agency detail pages.
 */
export function TopAgenciesSection() {
  const { recipientId } = useRecipientDetail()

  return (
    <SectionCard
      id="agencies"
      title="Top Awarding Agencies"
      animationDelay={100}
    >
      <QueryErrorBoundary
        title="Failed to load agencies"
        serviceUnavailableFallback={
          <RankingUnavailable height={280} rows={5} />
        }
      >
        <Suspense fallback={<SectionSkeleton height={280} bars={5} />}>
          <TopAgenciesContent id={recipientId} />
        </Suspense>
      </QueryErrorBoundary>
    </SectionCard>
  )
}

function TopAgenciesContent({ id }: { id: string }) {
  const { state } = useDetailFilter()
  const fiscalYear = state.fiscalYear === 'all' ? undefined : state.fiscalYear
  const { data: dashboard } = useSuspenseRecipientDashboard(id, fiscalYear)

  if (dashboard.topAgencies === null) {
    return <DeferredTopAgencies id={id} />
  }

  const agencies = dashboard.topAgencies

  if (agencies.length === 0) {
    return (
      <div className="flex h-[200px] items-center justify-center text-sm text-primary-400 dark:text-primary-500">
        No agencies found
      </div>
    )
  }

  return <AgenciesDataTable items={agencies} />
}

function DeferredTopAgencies({ id }: { id: string }) {
  const queries = useRecipientSectionQueries()
  const { state } = useDetailFilter()
  const fiscalYear = state.fiscalYear === 'all' ? undefined : state.fiscalYear
  const { data: agencies, isLoading } = useQuery(
    queries.analyticsAgencies(id, fiscalYear),
  )

  if (isLoading) return <SectionSkeleton height={280} bars={5} />
  if (!agencies || agencies.length === 0) {
    return (
      <div className="flex h-[200px] items-center justify-center text-sm text-primary-400 dark:text-primary-500">
        No agencies found
      </div>
    )
  }

  return <AgenciesDataTable items={agencies} />
}

/**
 * Memoized row component to prevent unnecessary re-renders (rerender-memo)
 */
const AgencyRow = memo(function AgencyRow({
  item,
  index,
  barWidth,
}: {
  item: AgencyItem
  index: number
  barWidth: number
}) {
  const pct = Number(item.percentage)

  const content = (
    <>
      {/* Background bar indicator */}
      <div className={BAR_CLASS} style={{ width: `${barWidth}%` }} />

      {/* Content (above the bar) */}
      <span className="relative w-6 shrink-0 text-center text-[11px] text-primary-400 dark:text-primary-500">
        {index + 1}
      </span>
      <span
        className="relative min-w-0 flex-1 truncate pr-2 text-[13px] text-primary-700 dark:text-primary-300"
        title={item.name}
      >
        {item.name}
      </span>
      <span className="relative w-16 shrink-0 text-right font-mono text-[11px] text-primary-600 dark:text-primary-400">
        {formatCurrency(Number(item.amount))}
      </span>
      <span className="relative w-12 shrink-0 text-right font-mono text-[11px] font-medium text-primary-900 dark:text-primary-100">
        {pct.toFixed(1)}%
      </span>
    </>
  )

  return <div className={ROW_CLASS}>{content}</div>
})

/**
 * Data table with inline bar indicators for agencies
 * Designed for scannability with rank, agency name, amount, and percentage in columns
 */
function AgenciesDataTable({ items }: { items: AgencyItem[] }) {
  // Memoize maxPercentage calculation (rerender-memo)
  const maxPercentage = useMemo(
    () => Math.max(...items.map((i) => Number(i.percentage)), 1),
    [items],
  )

  return (
    <div className="overflow-hidden">
      {/* Header row */}
      <div className="flex items-center border-b border-primary-200 bg-primary-50/50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-500 dark:border-primary-700 dark:bg-primary-800/50 dark:text-primary-400">
        <span className="w-6 shrink-0 text-center">#</span>
        <span className="min-w-0 flex-1">Agency</span>
        <span className="w-16 shrink-0 text-right">Amount</span>
        <span className="w-12 shrink-0 text-right">Share</span>
      </div>

      {/* Data rows - scrollable, compact */}
      <div className="max-h-[320px] divide-y divide-primary-100 overflow-y-auto dark:divide-primary-800">
        {items.map((item, index) => {
          const pct = Number(item.percentage)
          const barWidth = Math.max((pct / maxPercentage) * 100, 4)

          return (
            <AgencyRow
              key={item.code || item.name}
              item={item}
              index={index}
              barWidth={barWidth}
            />
          )
        })}
      </div>

      {/* Footer with count */}
      {items.length > 8 && (
        <div className="border-t border-primary-200 bg-primary-50/30 px-3 py-1.5 text-center text-xs text-primary-500 dark:border-primary-700 dark:bg-primary-800/30 dark:text-primary-400">
          {items.length} agencies total
        </div>
      )}
    </div>
  )
}
